<template>
  <div class="app-container">
    <el-alert
      title="雅斯特常规覆盖今天起 90 天；北京时间 06:00 前近期段额外补昨天，因此覆盖 91 个自然日。近期资源每 10 分钟同步。"
      type="info"
      :closable="false"
    />

    <el-tabs v-if="!fixedResource" v-model="active" class="resource-tabs" @tab-click="changeTab">
      <el-tab-pane v-for="tab in tabs" :key="tab.key" :label="tab.label" :name="tab.key" />
    </el-tabs>

    <div v-else class="resource-page-header">
      <div>
        <h2 class="resource-page-title">{{ currentDefinition.label }}</h2>
        <p class="resource-page-description">{{ currentDefinition.description }}</p>
      </div>
      <el-tag type="info" effect="plain">雅斯特酒店管理</el-tag>
    </div>

    <div v-if="active !== 'worker'" class="filter-container">
      <el-select
        v-for="item in activeFilters.filter(filterItem => filterItem.type)"
        :key="`${active}-${item.key}`"
        v-model="query[item.key]"
        :placeholder="item.label"
        clearable
        size="small"
        class="filter-item resource-filter"
        @change="reload"
      >
        <el-option
          v-for="option in statusOptions(item.type)"
          :key="`${item.type}-${option.value}`"
          :label="option.label"
          :value="option.value"
        />
      </el-select>
      <el-input
        v-for="item in activeFilters.filter(filterItem => !filterItem.type)"
        :key="`${active}-${item.key}`"
        v-model="query[item.key]"
        :placeholder="item.label"
        clearable
        size="small"
        class="filter-item resource-filter"
        @keyup.enter.native="reload"
      />
      <el-date-picker
        v-if="currentDefinition.dateRange"
        v-model="dateRange"
        type="daterange"
        value-format="yyyy-MM-dd"
        range-separator="至"
        start-placeholder="开始日期"
        end-placeholder="结束日期"
        size="small"
        class="filter-item date-filter"
      />
      <el-button type="primary" icon="el-icon-search" size="small" class="filter-item" @click="reload">查询</el-button>
      <el-button size="small" class="filter-item" @click="resetFilters">重置</el-button>
      <el-button v-if="active === 'hotel' && $canEdit" type="success" size="small" class="filter-item" @click="syncVisible = true">手工同步酒店</el-button>
      <el-button v-if="active === 'hotel' && $canEdit" type="warning" size="small" class="filter-item" :loading="listing" @click="batchUpHotels">批量上架有房酒店</el-button>
    </div>

    <template v-if="active === 'worker'">
      <div class="worker-actions">
        <el-button type="primary" icon="el-icon-refresh" size="small" @click="loadWorker">刷新运行状态</el-button>
      </div>
      <el-alert :title="runtimeSummary" :type="runtimeAlertType" :closable="false" show-icon style="margin-bottom: 12px" />
      <el-descriptions :column="4" border size="small" style="margin-bottom: 12px">
        <el-descriptions-item label="工作进程实例">
          <el-tag size="small" :type="runtimeStatus.worker_healthy ? 'success' : 'danger'">{{ runtimeStatus.active_worker_instances || 0 }} 个运行中</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="工作进程最后心跳">{{ formatUnix(runtimeStatus.last_worker_heartbeat_at) }}</el-descriptions-item>
        <el-descriptions-item label="任务调度器">
          <el-tag size="small" :type="runtimeStatus.task_scheduler_healthy ? 'success' : 'danger'">{{ runtimeStatus.task_scheduler_healthy ? '运行中' : '未运行或已超时' }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="调度器最后心跳">{{ formatUnix(runtimeStatus.task_scheduler_heartbeat_at) }}</el-descriptions-item>
      </el-descriptions>
      <el-descriptions title="PHP 同步生产者" :column="3" border size="small" style="margin-bottom: 12px">
        <el-descriptions-item v-for="item in producerItems" :key="item.key" :label="item.label">
          <el-tag size="small" :type="item.healthy ? 'success' : 'danger'">{{ item.healthy ? '正常' : '缺失或超时' }}</el-tag>
          <span style="margin-left: 8px">最近成功：{{ formatUnix(item.last_success_at) }}</span>
        </el-descriptions-item>
      </el-descriptions>
      <el-row :gutter="12" v-loading="loading">
        <el-col v-for="tier in workerTiers" :key="tier" :span="8" class="worker-card">
          <el-card>
            <div slot="header">{{ tierNames[tier] }}</div>
            <el-descriptions :column="1" border size="small">
              <el-descriptions-item label="排队任务数">{{ worker[tier] && worker[tier].queued || 0 }}</el-descriptions-item>
              <el-descriptions-item label="处理中任务数">{{ worker[tier] && worker[tier].processing || 0 }}</el-descriptions-item>
              <el-descriptions-item label="等待重试数">{{ worker[tier] && worker[tier].retry || 0 }}</el-descriptions-item>
              <el-descriptions-item label="死信任务数">{{ worker[tier] && worker[tier].dead || 0 }}</el-descriptions-item>
              <el-descriptions-item label="去重待完成数">{{ worker[tier] && worker[tier].unique || 0 }}</el-descriptions-item>
              <el-descriptions-item label="时效告警">
                <el-tag size="small" :type="metric(tier, 'sla_breach') === '1' ? 'danger' : 'success'">
                  {{ metric(tier, 'sla_breach') === '1' ? '已超时' : '正常' }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="理论耗时（秒）">{{ metric(tier, 'theoretical_seconds') || '-' }}</el-descriptions-item>
              <el-descriptions-item label="本轮入队时间">{{ formatUnix(metric(tier, 'queued_at')) }}</el-descriptions-item>
              <el-descriptions-item label="本轮截止时间">{{ formatUnix(metric(tier, 'deadline_at')) }}</el-descriptions-item>
              <el-descriptions-item label="最近完成时间">{{ formatUnix(metric(tier, 'completed_at')) }}</el-descriptions-item>
            </el-descriptions>
          </el-card>
        </el-col>
      </el-row>
    </template>

    <template v-else>
      <el-alert
        v-if="currentDefinition.statement && !statementReady"
        title="请选择 API 账户编号和完整对账日期范围后查询供应商对账单。"
        type="warning"
        :closable="false"
        show-icon
        style="margin-bottom: 12px"
      />
      <el-alert
        v-else
        :title="`${currentDefinition.label}共 ${total} 条；列表展示常用字段，点击“完整详情”可查看该资源的全部中文字段和供应商原始数据。`"
        type="success"
        :closable="false"
        style="margin-bottom: 12px"
      />
      <el-table
        :data="rows"
        v-loading="loading"
        border
        stripe
        size="small"
        empty-text="暂无资源数据"
        style="width: 100%"
      >
        <el-table-column
          v-for="column in currentTableColumns"
          :key="column.key"
          :prop="column.key"
          :label="column.label"
          :width="column.width"
          :min-width="column.minWidth"
          :fixed="column.fixed"
          align="center"
          show-overflow-tooltip
        >
          <template slot-scope="scope">
            <el-image
              v-if="column.type === 'image' && scope.row[column.key]"
              :src="scope.row[column.key]"
              :preview-src-list="[scope.row[column.key]]"
              fit="cover"
              class="resource-image"
            />
            <el-tag v-else-if="statusTag(column, scope.row[column.key])" size="small" :type="statusTag(column, scope.row[column.key]).type">
              {{ statusTag(column, scope.row[column.key]).label }}
            </el-tag>
            <el-button v-else-if="column.type === 'json' || column.type === 'longText'" type="text" @click="showDetails(scope.row)">查看详情</el-button>
            <span v-else>{{ displayValue(scope.row[column.key], column) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" fixed="right" :width="active === 'hotel' ? 150 : 100" align="center">
          <template slot-scope="scope">
            <el-button type="text" @click="showDetails(scope.row)">完整详情</el-button>
            <el-button
              v-if="active === 'hotel' && $canEdit && canUp(scope.row)"
              type="text"
              :loading="listingHotelCode === scope.row.hotel_code"
              @click="upHotel(scope.row)"
            >上架</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-container">
        <el-pagination
          v-show="total > 0"
          background
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          :current-page.sync="query.page"
          :page-size.sync="query.limit"
          :page-sizes="[10, 20, 50, 100]"
          @current-change="loadList"
          @size-change="reload"
        />
      </div>
    </template>

    <el-dialog :title="detailTitle" :visible.sync="detailVisible" width="90%" top="5vh">
      <el-descriptions v-if="detailRow" :column="2" border size="small" class="resource-detail">
        <el-descriptions-item v-for="column in currentDetailColumns" :key="column.key" :label="column.label">
          <el-image
            v-if="column.type === 'image' && detailRow[column.key]"
            :src="detailRow[column.key]"
            :preview-src-list="[detailRow[column.key]]"
            fit="contain"
            class="detail-image"
          />
          <el-tag v-else-if="statusTag(column, detailRow[column.key])" size="small" :type="statusTag(column, detailRow[column.key]).type">
            {{ statusTag(column, detailRow[column.key]).label }}
          </el-tag>
          <pre v-else-if="column.type === 'json' || column.type === 'longText'" class="json-preview">{{ prettyValue(detailRow[column.key]) }}</pre>
          <span v-else>{{ displayValue(detailRow[column.key], column) }}</span>
        </el-descriptions-item>
      </el-descriptions>
      <el-alert
        v-if="detailUnknownFields.length"
        :title="`检测到 ${detailUnknownFields.length} 个后端新增字段，请补充中文字段定义：${detailUnknownFields.join('、')}`"
        type="warning"
        :closable="false"
        show-icon
        style="margin-top: 12px"
      />
      <span slot="footer"><el-button type="primary" @click="detailVisible = false">关闭</el-button></span>
    </el-dialog>

    <el-dialog title="手工同步雅斯特酒店（最长 90 天）" :visible.sync="syncVisible" width="520px">
      <el-form :model="syncForm" label-width="120px">
        <el-form-item label="API 账户编号"><el-input-number v-model="syncForm.api_account_id" :min="1" /></el-form-item>
        <el-form-item label="酒店编码"><el-input v-model="syncForm.hotel_code" /></el-form-item>
        <el-form-item label="同步日期"><el-date-picker v-model="syncForm.dates" type="daterange" value-format="yyyy-MM-dd" range-separator="至" start-placeholder="开始日期" end-placeholder="结束日期" /></el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="syncVisible = false">取消</el-button>
        <el-button type="primary" :loading="syncing" @click="syncHotel">提交同步队列</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request'
import { YASITE_TIER_NAMES, canUpYaSiteHotel, yaSiteRuntimeSummary } from './resourceOperationRules'
import {
  YASITE_RESOURCE_DEFINITIONS,
  yaSiteAllFieldsCovered,
  yaSiteDisplayValue,
  yaSitePrettyValue,
  yaSiteStatusTag,
  yaSiteStatusOptions,
  yaSiteTableColumns,
} from './resourceDisplayConfig'

export default {
  name: 'YaSiteResourceList',
  props: {
    resource: { type: String, default: '' },
  },
  data() {
    return {
      definitions: YASITE_RESOURCE_DEFINITIONS,
      tabs: Object.keys(YASITE_RESOURCE_DEFINITIONS).map(key => ({ key, label: YASITE_RESOURCE_DEFINITIONS[key].label })),
      active: YASITE_RESOURCE_DEFINITIONS[this.resource] ? this.resource : 'order',
      query: { page: 1, limit: 20 },
      dateRange: [],
      rows: [],
      total: 0,
      loading: false,
      worker: {},
      workerTiers: ['recent', 'midterm', 'forward', 'batch_up', 'maintenance'],
      tierNames: YASITE_TIER_NAMES,
      syncVisible: false,
      syncing: false,
      listing: false,
      listingHotelCode: '',
      syncForm: { api_account_id: null, hotel_code: '', dates: [] },
      detailVisible: false,
      detailRow: null,
    }
  },
  computed: {
    fixedResource() { return Boolean(this.resource && this.definitions[this.resource]) },
    currentDefinition() { return this.definitions[this.active] || {} },
    currentTableColumns() { return yaSiteTableColumns(this.currentDefinition) },
    currentDetailColumns() { return this.currentDefinition.columns || [] },
    activeFilters() { return this.currentDefinition.filters || [] },
    statementReady() {
      return !this.currentDefinition.statement || (Number(this.query.api_account_id) > 0 && this.dateRange && this.dateRange.length === 2)
    },
    detailUnknownFields() { return yaSiteAllFieldsCovered(this.currentDefinition, this.detailRow) },
    detailTitle() {
      if (!this.detailRow) return `${this.currentDefinition.label || '资源'}完整详情`
      const identity = this.currentDefinition.identity
      const value = identity ? this.detailRow[identity] : ''
      return `${this.currentDefinition.label || '资源'}完整详情${value === undefined || value === null || value === '' ? '' : ` - ${value}`}`
    },
    runtimeStatus() { return this.worker.runtime || {} },
    producerStatus() { return this.worker.producer || {} },
    producerItems() {
      const labels = { recent: '近期资源（每 10 分钟）', midterm: '中期资源（每小时）', forward: '远期资源（每 6 小时）', batch_up: '批量上架（每分钟）', catalog: '基础目录（每日）', cleanup: '历史清理（每日）' }
      const items = this.producerStatus.items || {}
      return Object.keys(labels).map(key => ({ key, label: labels[key], ...(items[key] || {}) }))
    },
    runtimeAlertType() { return this.runtimeStatus.overall_healthy && this.producerStatus.overall_healthy ? 'success' : 'error' },
    runtimeSummary() { return yaSiteRuntimeSummary(this.runtimeStatus, this.producerStatus) },
  },
  watch: {
    resource(value) {
      if (!value || !this.definitions[value] || value === this.active) return
      this.active = value
      this.query = { page: 1, limit: this.query.limit || 20 }
      this.dateRange = []
      this.rows = []
      this.total = 0
      this.detailVisible = false
      this.detailRow = null
      this.reload()
    },
  },
  created() { this.loadList() },
  methods: {
    changeTab() {
      this.query = { page: 1, limit: 20 }
      this.dateRange = []
      this.rows = []
      this.total = 0
      this.detailVisible = false
      this.detailRow = null
      this.reload()
    },
    resetFilters() {
      this.query = { page: 1, limit: this.query.limit || 20 }
      this.dateRange = []
      this.loadList()
    },
    reload() {
      this.query.page = 1
      if (this.active === 'worker') this.loadWorker()
      else this.loadList()
    },
    requestParams() {
      const allowed = new Set((this.currentDefinition.filters || []).map(item => item.key))
      const params = { page: this.query.page, limit: this.query.limit }
      Object.keys(this.query).forEach(key => {
        const value = this.query[key]
        if (allowed.has(key) && value !== '' && value !== null && value !== undefined) params[key] = value
      })
      if (this.currentDefinition.dateRange && this.dateRange && this.dateRange.length === 2) {
        if (this.currentDefinition.statement) {
          params.start_date = this.dateRange[0]
          params.end_date = this.dateRange[1]
        } else {
          params.start_at = this.dateRange[0]
          params.end_at = this.dateRange[1]
        }
      }
      return params
    },
    loadList() {
      if (!this.currentDefinition.url) return
      if (!this.statementReady) {
        this.rows = []
        this.total = 0
        return
      }
      this.loading = true
      request({ url: this.currentDefinition.url, method: 'get', params: this.requestParams() })
        .then(response => {
          const payload = response.data || {}
          if (this.currentDefinition.statement) {
            this.rows = Array.isArray(payload.orders) ? payload.orders : []
            this.total = Number(payload.total || this.rows.length)
          } else {
            this.rows = Array.isArray(payload.data) ? payload.data : (Array.isArray(payload.items) ? payload.items : [])
            this.total = Number(payload.total || 0)
          }
        })
        .finally(() => { this.loading = false })
    },
    loadWorker() {
      this.loading = true
      request({ url: '/api/backend/yaSite/worker/status', method: 'get' })
        .then(response => { this.worker = response.data || {} })
        .finally(() => { this.loading = false })
    },
    showDetails(row) {
      this.detailRow = row
      this.detailVisible = true
    },
    statusTag(column, value) { return yaSiteStatusTag(column, value) },
    statusOptions(type) { return yaSiteStatusOptions(type) },
    displayValue(value, column) { return yaSiteDisplayValue(value, column) },
    prettyValue(value) { return yaSitePrettyValue(value) },
    syncHotel() {
      const code = String(this.syncForm.hotel_code || '').trim()
      if (!this.syncForm.api_account_id || !code) {
        this.$message.warning('请填写 API 账户编号和酒店编码')
        return
      }
      const data = { api_account_id: this.syncForm.api_account_id, hotel_code: code }
      if (this.syncForm.dates && this.syncForm.dates.length === 2) {
        const start = new Date(this.syncForm.dates[0])
        const end = new Date(this.syncForm.dates[1])
        if ((end - start) / 86400000 >= 90) {
          this.$message.warning('雅斯特仅支持最长 90 天')
          return
        }
        data.start_date = this.syncForm.dates[0]
        data.end_date = this.syncForm.dates[1]
      }
      this.syncing = true
      request({ url: '/api/backend/yaSite/hotel/sync', method: 'post', data })
        .then(() => {
          this.$message.success('已进入近期同步队列')
          this.syncVisible = false
          this.loadWorker()
        })
        .finally(() => { this.syncing = false })
    },
    canUp(row) { return canUpYaSiteHotel(row) },
    upHotel(row) {
      const hotelCode = String(row.hotel_code || '').trim()
      if (!hotelCode) return
      this.listingHotelCode = hotelCode
      request({ url: '/api/backend/yaSite/hotel/up', method: 'post', data: { hotel_code: hotelCode } })
        .then(response => {
          const queued = Number((response.data || {}).queued || 0)
          this.$message.success(queued > 0 ? '已加入上架队列' : '酒店已在上架队列中')
          this.loadWorker()
        })
        .finally(() => { this.listingHotelCode = '' })
    },
    batchUpHotels() {
      this.$confirm('将所有有房态且尚未映射的雅斯特酒店加入上架队列，是否继续？', '批量上架', { type: 'warning' })
        .then(() => {
          this.listing = true
          return request({ url: '/api/backend/yaSite/hotel/batchUp', method: 'post', data: {} })
        })
        .then(response => {
          const data = response.data || {}
          this.$message.success(`候选 ${Number(data.candidates || 0)} 家，新增入队 ${Number(data.queued || 0)} 家`)
          this.loadWorker()
        })
        .catch(error => {
          if (error !== 'cancel' && error !== 'close') this.$message.error(error.message || '批量上架请求失败')
        })
        .finally(() => { this.listing = false })
    },
    metric(tier, key) { return this.worker[tier] && this.worker[tier].metrics ? this.worker[tier].metrics[key] : '' },
    formatUnix(value) {
      const seconds = Number(value || 0)
      if (!seconds) return '-'
      const date = new Date(seconds * 1000)
      if (Number.isNaN(date.getTime())) return '-'
      const pad = number => String(number).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    },
  },
}
</script>

<style scoped>
.resource-tabs { margin-top: 16px; }
.resource-page-header { display: flex; align-items: center; justify-content: space-between; margin: 16px 0 14px; padding: 18px 20px; border: 1px solid #ebeef5; border-left: 4px solid #409eff; border-radius: 4px; background: #fff; box-shadow: 0 1px 4px rgba(0, 0, 0, .04); }
.resource-page-title { margin: 0; color: #303133; font-size: 20px; font-weight: 600; }
.resource-page-description { margin: 8px 0 0; color: #909399; font-size: 13px; line-height: 1.5; }
.resource-filter { width: 160px; }
.date-filter { width: 260px; }
.worker-actions { margin-bottom: 12px; }
.worker-card { margin-bottom: 12px; }
.resource-image { width: 72px; height: 48px; border-radius: 3px; vertical-align: middle; }
.detail-image { width: 320px; max-height: 220px; }
.json-preview { margin: 0; max-height: 320px; overflow: auto; white-space: pre-wrap; word-break: break-all; font-family: Consolas, Monaco, monospace; font-size: 12px; line-height: 1.5; }
.resource-detail ::v-deep .el-descriptions-item__label { width: 160px; color: #303133; }
</style>
